import axios from 'axios';

import { SERVICEBASEURL } from 'enums/serviceBaseUrls';
import { Optional } from 'functions/promiseExtensions';

export async function callApi(request: Request): Promise<Optional<ResponseData>> {
	const url = `${SERVICEBASEURL.AI}/import/recipe/?inAir=false`;

	return axios
		.post(url, request)
		.toOptionalFiltered((x) => x.data.code === 200)
		.mapAxiosResponse((x) => x.data.result);
}

export type Request = {
	recipeJson: RecipeJson | null;
	recipeUrl: string | null;
};

export type Response = {
	result: ResponseData;
};

export type ResponseData = {
	accessoryProducts: AccessoryProducts[];
	activityTimes: ActivityTimes[];
	allergens: Allergens[];
	calories: number | null;
	carbonDioxide: CarbonDioxide[];
	carbonDioxideLabel: CarbonDioxideLabel;
	carbonDioxideValue: number | null;
	categories: Categories[];
	entryLanguage: EntryLanguage;
	fullData: FullData;
	hasNutriScore: boolean | null;
	id: string | null;
	image: Image;
	images: Image[];
	incorporationShares: IncorporationShares[];
	ingredientCalories: number | null;
	ingredientProducts: IngredientProducts[];
	ingredientWeight: number | null;
	ingredientsTranslations: Translation;
	isSeoActive: boolean | null;
	notesTranslations: Translation;
	nutriScore: NutriScore;
	nutriScoreCategory: 'StandardFood' | 'Cheese' | 'Beverage' | 'AddedFat' | 'Water';
	nutrientValues: NutrientValues[];
	parts: number | null;
	persons: number | null;
	price: number | null;
	scaledParts: number | null;
	scaledPersons: number | null;
	seasons: Seasons[];
	segmentMedia: SegmentMediaCollection;
	seoTranslations: Translation;
	shortDescriptionTranslations: Translation;
	source: string | null;
	status: Status;
	stepsTranslations: Translation;
	systemStatus: 'New' | 'Published';
	tags: Tags[];
	titleTranslations: Translation;
	videos: Video[];
	weight: number | null;
};

export type RecipeJson = {
	author: string;
	category: string[];
	cookTime: number;
	cuisine: string;
	description: string;
	images: string[];
	videos: string[];
	ingredients: string[];
	instructions: string;
	keywords: string[];
	language: string;
	prepTime: number;
	title: string;
	totalTime: number;
	yields: string;
};

export type FullData = {
	author: string;
	category: string[];
	cookTime: number;
	cuisine: string;
	description: string;
	images: string[];
	ingredients: string[];
	instructions: string;
	keywords: string[];
	language: string;
	prepTime: number;
	title: string;
	totalTime: number;
	videos: string[];
	yields: string;
};

export type Image = {
	availableSizes: number[];
	channels: ChannelItem[];
	id: string;
	url: string;
};

export type ChannelItem = {
	iconSvgUrl: string | null;
	id: string;
	name: string;
};

export type Video = {
	availableSized: number[];
	channels: ChannelItem[];
	id: string;
	url: string;
};

export type SegmentMedia = {
	availableSizes: number[];
	id: string;
	url: string;
};

export type SegmentMediaCollection = {
	[key: string]: {
		images: SegmentMedia[];
		videos: SegmentMedia[];
	};
};

export type EntryLanguage = {
	id: string;
	description: string;
};

export type Status = {
	id: string;
	color: string;
	order: number;
	name: string;
};

export type NutriScore = {
	label: string;
	color: string;
};

export type CarbonDioxideLabel = {
	label: string;
	color: string;
};

export type Translation = {
	[key: string]: string;
};

export type Tags = {
	id: string;
	iconSvgUrl?: string | null;
	display: boolean;
	name: string;
	tagCategory: TagCategory | null;
};

export type TagCategory = {
	id: string;
	name: string;
};

export type ActivityTimes = {
	id: string;
	name: string;
	timeUnit: TimeUnit;
	timeValue: number;
};

export type TimeUnit = {
	id: string;
	description: string;
};

export type Seasons = {
	id: string;
	iconSvgUrl: string | null;
	name: string;
	display: boolean;
	associatedMonthsFlag: number | null;
};

export type Allergens = {
	id: string;
	iconSvgUrl: string | null;
	name: string;
	display: boolean;
	logic: 'Positive' | 'Negative' | 'MayContain';
};

export type NutrientValues = {
	nutrientId: string;
	nutrient: string;
	categoryId: string;
	category: string;
	total: number;
	unitId: string;
	unit: string;
	isMacroNutrient: boolean;
};

export type Categories = {
	id: string;
	name: string;
};

export type CarbonDioxide = {
	originLocationId: string;
	origin: string;
	productionMethodId: string;
	productionMethod: string;
	value: number;
};

export type IncorporationShares = {
	segmentIndex: number;
	ingredientIndex: number;
	incorporationShare: number;
	quantity: number | null;
	unitId: string | null;
	ingredientId: string;
};

export type IngredientProducts = {
	id: string;
	externalId: string | null;
	brand: string | null;
	producer: string | null;
	price: number | null;
	imageUrl: string | null;
	externalUrl: string | null;
	quantity: string | null;
	nameTranslations: Translation;
};

export type AccessoryProducts = {
	id: string;
	externalId: string | null;
	brand: string | null;
	producer: string | null;
	price: number | null;
	imageUrl: string | null;
	externalUrl: string | null;
	quantity: string | null;
	nameTranslations: Translation;
};
